import type { ReactNode } from 'react'
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { AttributeDefinition, GarmentCard } from '@fitting-room/contracts'
import { GarmentFacts } from './GarmentFacts'
import { bestImage } from '../lib/media'
import { usePrefersReducedMotion } from '../lib/motion'
import { uiStrings } from '../lib/strings'
import { text } from '../lib/text'

/**
 * The deck: one gown, the size of the screen, scrolled like a feed.
 *
 * Three shapes in three weeks. It was a rack in perspective, swiped sideways,
 * with the neighbours turned away from the viewer. It became a column of cards
 * a thumb scrolls through. The boutique looked at that and named the thing she
 * had meant all along — "like scrolling Instagram" — and the difference
 * between the second and the third is not the gesture, which was already
 * right. It is what else is on the screen.
 *
 * So: nothing is. The photograph is edge to edge and floor to ceiling, and the
 * name, the silhouette, the neckline and the sizes are behind one mark in the
 * corner. A bride looking for a dress is looking at dresses; the words are for
 * the moment she stops on one, and printing them under all two hundred is the
 * same mistake as printing the price on a gallery wall.
 *
 * The chrome that was above it — which catalogue, which filters — folds away
 * with the words. It was a mark in the photograph's top right until the grid
 * arrived; it is in the step's own bar above the feed now, beside the switch
 * between the grid and this, because it narrows both. Same idiom as before:
 * what narrows the gowns is up where the screen begins, what fills a rail is
 * down where a thumb rests.
 *
 * It is also where a gown tapped in the grid opens, at that gown — so it may
 * mount at any `index`, not only the first.
 *
 * What is on the photograph, bottom right, where a thumb already is:
 *
 * - **The marks**, one per slot, handed in by the step (`actions`) because
 *   which slots exist and what they are called is server-resolved. They are
 *   outlines until they are chosen and filled when they are, which is a state
 *   readable without colour.
 * - **The count**, and only on the tap that changes it: a pill that slides out
 *   to the left of the mark, says "Must try 1/4", and goes. The rails below
 *   the feed are where a number lives permanently; over a photograph it is an
 *   answer to what she just did, not furniture.
 * - **The way to the words**, which is this file's own button. It opens the
 *   panel over the bottom of the photograph, and the next gown closes it —
 *   scrolling is the gesture that means "not this one", so the words should
 *   not follow her down the feed.
 */
export function GarmentDeck({
  cards,
  attributes,
  locale,
  index,
  onIndexChange,
  actions,
  sheetOpen = false,
  onFactsOpen,
}: {
  readonly cards: readonly GarmentCard[]
  readonly attributes: readonly AttributeDefinition[]
  readonly locale: string
  readonly index: number
  readonly onIndexChange: (next: number) => void
  /** The pick marks for the gown in front. Rendered under this file's own. */
  readonly actions?: (card: GarmentCard) => ReactNode
  /**
   * Whether the step's tools are open over the top of the gown. The step draws
   * them — they cover the grid too — and this file only needs to know, so the
   * gown's own words can get out of their way.
   */
  readonly sheetOpen?: boolean
  /**
   * Called when the gown's own words open, so the step can put its tools away.
   * The two panels cover opposite ends of the same photograph and both of them
   * open at once leaves none of it — see the effect below for the other half.
   */
  readonly onFactsOpen?: () => void
}) {
  const t = uiStrings(locale)
  const reduced = usePrefersReducedMotion()
  const [enlarged, setEnlarged] = useState(false)
  const [facts, setFacts] = useState(false)
  /**
   * Photographs that did not arrive.
   *
   * The media URLs are upstream's and upstream's expire — a catalogue imported
   * before the boutique's own media store was configured serves a feed of
   * 410s. A gown whose photograph cannot be fetched is the same thing as a
   * gown with none, so it falls back to its name rather than to the browser's
   * broken-image glyph, two hundred times.
   */
  const [broken, setBroken] = useState<ReadonlySet<string>>(() => new Set())
  const feedRef = useRef<HTMLDivElement | null>(null)
  /** The photo control the enlarged view was opened from, so focus can go home. */
  const opener = useRef<HTMLButtonElement | null>(null)
  const closeRef = useRef<HTMLButtonElement | null>(null)
  /**
   * The card the feed and the page last agreed on.
   *
   * It is what keeps the two directions apart. An index the feed itself
   * reported — she scrolled — must never be scrolled to again: the correction
   * would land in the middle of her own flick, fighting the momentum with a
   * smooth scroll to where she already is. An index that arrives from anywhere
   * else — the arrows, a filter landing and resetting the deck to the top — is
   * one the feed has to go to.
   */
  const settled = useRef(index)
  /**
   * A scroll this component issued, and where it is going.
   *
   * Without it the arrows stutter. `scrollTo` with `behavior: 'smooth'` fires
   * scroll events from every position on the way, and the first of them is
   * still rounding to the gown she is LEAVING — so the deck reported that gown
   * back and the counter flicked to it for one press. Reports are ignored
   * until the trip lands, and the deadline is there so that a scroll the
   * browser abandons — she grabbed the feed mid-flight — cannot leave the
   * reporting switched off.
   */
  const trip = useRef<{ readonly top: number; readonly until: number } | null>(null)

  const clamp = useCallback((next: number) => Math.max(0, Math.min(cards.length - 1, next)), [cards.length])

  useEffect(() => {
    if (index > cards.length - 1) onIndexChange(clamp(index))
  }, [cards.length, index, clamp, onIndexChange])

  /*
   * The words belong to the gown she asked them about.
   *
   * Scrolling is how she says "not this one", so the panel closes on the way
   * to the next gown rather than travelling with her — and a filter landing,
   * which resets the deck to the top, closes it for the same reason.
   */
  useEffect(() => {
    setFacts(false)
  }, [index])

  /*
   * And they never share the screen.
   *
   * The words rise from the bottom and the tools drop from the top, and with
   * both out there is a strip of gown left between them — which is two panels
   * and no dress, on the screen whose whole argument is the dress. Opening
   * either closes the other: this half here, the other half through
   * `onFactsOpen`, because the two states are owned by the two files that
   * each need to close one.
   */
  useEffect(() => {
    if (sheetOpen) setFacts(false)
  }, [sheetOpen])

  /**
   * How far apart two slides are: one slide's height plus the gap under it,
   * measured rather than computed.
   *
   * Fractional, off `getBoundingClientRect`, because the position of the nth
   * slide is n times this: `offsetHeight` is rounded to the whole pixel, and
   * over a page of 200 gowns half a pixel of rounding is a hundred pixels of
   * error — the counter reading one gown while another is on the screen.
   *
   * The gap is IN it. It was the height alone for as long as the feed only
   * ever started at the top and moved one gown at a time — the snap put each
   * gown right, and 12px a gown of drift only rounded to the wrong one past
   * the twenty-seventh. Opened at gown forty from the grid it read the next
   * gown as the one in front: the visible one went inert and its marks were
   * drawn on a gown off the screen. One stride, used for every position this
   * file reads or writes.
   */
  const slideStride = (feed: HTMLElement): number => {
    const first = feed.firstElementChild
    if (!(first instanceof HTMLElement)) return feed.clientHeight
    const gap = parseFloat(getComputedStyle(first).marginBottom) || 0
    return first.getBoundingClientRect().height + gap
  }

  const onScroll = () => {
    const feed = feedRef.current
    if (!feed) return
    const height = slideStride(feed)
    if (height <= 0) return
    const flight = trip.current
    if (flight) {
      // A pixel and a half of tolerance: a slide height is fractional and a
      // smooth scroll need not land on the exact number it was given.
      if (Math.abs(feed.scrollTop - flight.top) > 1.5 && Date.now() < flight.until) return
      trip.current = null
    }
    const next = clamp(Math.round(feed.scrollTop / height))
    if (next === settled.current) return
    settled.current = next
    onIndexChange(next)
  }

  useEffect(() => {
    const feed = feedRef.current
    if (!feed) return
    if (index === settled.current) return
    settled.current = index
    const height = slideStride(feed)
    if (height <= 0) return
    const top = index * height
    trip.current = { top, until: Date.now() + 1200 }
    // `auto` under reduced motion is the behavioural difference the setting
    // asks for: she still gets to the gown, the room just does not travel.
    feed.scrollTo({ top, behavior: reduced ? 'auto' : 'smooth' })
  }, [index, reduced])

  /*
   * Where it opens.
   *
   * `settled` starts at the index it was handed, so the effect above never
   * moves a feed that has just mounted — which was right while it always
   * mounted at the first gown. It mounts mid-catalog now: from the grid, at
   * the gown she was looking at when she switched. So
   * the first position is set once, before paint and without travelling, and
   * from then on the two directions above are the only ones.
   */
  const opening = useRef(index)
  useLayoutEffect(() => {
    const feed = feedRef.current
    if (!feed) return
    const at = opening.current
    const stride = slideStride(feed)
    if (at > 0 && stride > 0) feed.scrollTop = at * stride
  }, [])

  const focused = cards[index]
  const focusedPhoto = focused ? bestImage(focused.garment.media) : null
  const focusedImage = focusedPhoto && !broken.has(focusedPhoto.src) ? focusedPhoto : null
  /*
   * One flag, so the effect below and the dialog itself cannot disagree about
   * whether it is open. The JSX still spells the two terms out after it —
   * a boolean narrows nothing, and `focused` and `focusedImage` have to be
   * proven present to be read — but this is the term that decides.
   */
  const showEnlarged = enlarged && focused !== undefined && focusedImage !== null

  useEffect(() => {
    if (showEnlarged) {
      closeRef.current?.focus()
      return
    }
    /* The deck moved out from under an open photo — a filter landed, the page
       came back shorter. Close it, rather than leave it waiting to reappear
       over a gown she never opened. */
    setEnlarged(false)
  }, [showEnlarged])

  const closeEnlarged = useCallback(() => {
    setEnlarged(false)
    opener.current?.focus()
  }, [])

  return (
    /*
      Not `items-center`. The feed bleeds out through the room's gutter with a
      negative margin, and a centred flex item with an explicit width takes the
      margin off one side and puts it back on the other — which is a
      full-bleed photograph, centred, at exactly the width it was trying to
      escape. The counter row below centres its own contents.
    */
    <div className="flex min-h-0 w-full flex-1 flex-col">
      <div
        ref={feedRef}
        className="fr-feed"
        role="group"
        aria-roledescription="carousel"
        aria-label={focused ? t.ofCount(index + 1, cards.length) : ''}
        tabIndex={0}
        onScroll={onScroll}
        onKeyDown={(event) => {
          const go = (next: number) => {
            event.preventDefault()
            onIndexChange(clamp(next))
          }
          // Down and up are what this screen is; right and left are kept
          // because they were the deck's keys before it turned, and a bride
          // who learned them on Tuesday should not find them gone.
          if (event.key === 'ArrowDown' || event.key === 'ArrowRight' || event.key === 'PageDown') go(index + 1)
          else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft' || event.key === 'PageUp') go(index - 1)
          else if (event.key === 'Home') go(0)
          else if (event.key === 'End') go(cards.length - 1)
        }}
      >
        {cards.map((card, cardIndex) => {
          const isFocused = cardIndex === index
          const name = text(card.garment.name, locale)
          const photo = bestImage(card.garment.media)
          const image = photo && !broken.has(photo.src) ? photo : null
          return (
            /*
              Every gown in the page is a real slide with a real height — that
              is what makes the scrollbar honest and the nth position
              arithmetic rather than a guess — but only the one in front is
              readable. The rest stay `inert`: a screen reader that met 200
              gowns' worth of names, prices and sizes in the tab order would be
              reading a catalogue, and what she is being asked is which of THIS
              one she wants. The arrows and the counter below are how that
              moves without a mouse.
            */
            <div key={card.garment.id} className="fr-feed-slide" aria-hidden={!isFocused} inert={!isFocused}>
              {image ? (
                /*
                  The photograph is also the way to see the whole photograph:
                  the frame crops to the shape of the phone, and a tap opens
                  what the crop took off. It is a button only on the gown in
                  front — the others are scenery until she scrolls to them.
                */
                <button
                  type="button"
                  className="fr-feed-photo"
                  aria-label={t.enlargePhoto(name)}
                  onClick={(event) => {
                    opener.current = event.currentTarget
                    setEnlarged(true)
                  }}
                >
                  <img
                    src={image.src}
                    alt=""
                    loading="lazy"
                    draggable={false}
                    onError={() => setBroken((current) => new Set(current).add(image.src))}
                  />
                </button>
              ) : (
                <div className="fr-feed-photo fr-feed-photo-empty">
                  <span className="fr-eyebrow">{name}</span>
                </div>
              )}

              {/* The corner the light falls off, so a hairline mark is never a
                  hairline on a white train. Under the controls, over the photo,
                  and deaf to the pointer. */}
              <div className="fr-feed-corner" aria-hidden="true" />

              {isFocused ? (
                <>
                  {facts ? (
                    <div className="fr-feed-facts">
                      <GarmentFacts card={card} attributes={attributes} locale={locale} variant="panel" />
                    </div>
                  ) : null}

                  <div className="fr-feed-controls">
                    <button
                      type="button"
                      className="fr-feed-mark"
                      data-selected={facts}
                      aria-pressed={facts}
                      aria-label={facts ? t.hideDetails : t.showDetails}
                      onClick={() => {
                        if (!facts) onFactsOpen?.()
                        setFacts((open) => !open)
                      }}
                    >
                      {/* The information glyph without its ring: the button is
                          the ring, and two of them is a target. */}
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" aria-hidden="true">
                        <path d="M12 11v6" />
                        <path d="M12 7.4v.2" />
                      </svg>
                    </button>
                    {actions?.(card)}
                  </div>
                </>
              ) : null}
            </div>
          )
        })}
      </div>

      {/*
        The enlarged photograph is a SIBLING of the feed, never a descendant.
        A slide is a scrolling box inside a clipping one, so a dialog in there
        would scroll away with the gown it was opened from and be cut off by
        the feed's own overflow on the way out. Kept here rather than portalled
        to `document.body` because the tenant's `--fr-*` tokens are written
        onto a wrapper element by `BrandingScope`, and a portal out of the room
        would leave the dialog wearing the fallback palette of a boutique it
        does not belong to.
      */}
      {showEnlarged && focused && focusedImage ? (
        <div
          className="fr-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={text(focused.garment.name, locale)}
          /* So a click on the picture itself lands somewhere Escape is heard. */
          tabIndex={-1}
          onPointerDown={(event) => {
            if (event.target === event.currentTarget) closeEnlarged()
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.preventDefault()
              closeEnlarged()
              return
            }
            // One control in the dialog, so the whole focus trap is this line.
            if (event.key === 'Tab') {
              event.preventDefault()
              closeRef.current?.focus()
            }
          }}
        >
          {/*
            The same derivative the feed is already showing, so opening it
            costs a bride on a phone nothing: it is decoded and in cache, and
            "as large as the viewport allows" is a layout question, not a
            request for a bigger file. Uncropped, which is the point — the feed
            cuts a portrait photograph to the shape of a phone.
          */}
          <img src={focusedImage.src} alt={focusedImage.alt || text(focused.garment.name, locale)} />
          <button
            ref={closeRef}
            type="button"
            className="fr-iconbtn fr-lightbox-close"
            aria-label={t.closePhoto}
            onClick={closeEnlarged}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      ) : null}

      {/*
        Where she is, said only to whoever cannot see it.

        There were two arrows and a counter under the feed. They went with
        everything else that was under the feed — the screen is a gown from
        edge to edge and the page does not scroll — and what they were for is
        covered twice over without them: the thumb scrolls, and the feed itself
        takes the arrow keys, Home and End when it has focus. What could not
        survive their going is the ANSWER to "which of these am I on", which a
        sighted customer reads off the photograph and a screen reader cannot.
        So it is said here, politely, and takes up no room at all.
      */}
      <span className="sr-only" aria-live="polite">
        {focused ? t.ofCount(index + 1, cards.length) : ''}
      </span>
    </div>
  )
}
